import React, { useRef } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

import { useTranslation } from 'react-i18next';

/*
 * The column picker shared by the source wizards (BigQuery, AppMetrica): a
 * flat, fast list of columns with a tick each, a filter above it and a docked
 * card beside it describing the column under the mouse. A column is
 * `{path, type, description, repeated?, carried_as?}`; the list knows nothing
 * about where the columns came from.
 */

// The column type (BigQuery's spellings and the catalog kinds string /
// integer / boolean / datetime), coloured so a list of two hundred columns can
// be scanned by shape rather than read. The tones are MUI palette roles, resolved once in
// ColumnList below, so they follow the console's light and dark themes instead
// of being fixed hex values.
const TYPE_TONES = {
  STRING: 'info',
  BYTES: 'info',
  INT64: 'success',
  INTEGER: 'success',
  FLOAT64: 'success',
  FLOAT: 'success',
  NUMERIC: 'success',
  DECIMAL: 'success',
  BIGNUMERIC: 'success',
  BIGDECIMAL: 'success',
  DATE: 'warning',
  DATETIME: 'warning',
  TIMESTAMP: 'warning',
  TIME: 'warning',
  BOOL: 'secondary',
  BOOLEAN: 'secondary',
};

const TONES = ['info', 'success', 'warning', 'secondary', 'primary'];

export function typeBadge(column) {
  // What the loader will hold, not what BigQuery calls it: a STRUCT and a
  // repeated INT64 both arrive as JSON text, and that is the fact that decides
  // how the column is queried downstream. The BigQuery type is in the tooltip.
  if (column.carried_as === 'json') return { label: 'json', tone: 'primary' };
  const type = (column.type || '').toUpperCase();
  return { label: type.toLowerCase(), tone: TYPE_TONES[type] || 'muted' };
}

/**
 * One column: tick, name, type, and whatever the table says about it.
 *
 * A row, not a cell in a wrapped grid (owner, 13.09): GA4 paths run past sixty
 * characters -- session_traffic_source_last_click.manual_campaign.source --
 * and in a grid they overlapped each other into an unreadable smear.
 */
// A dotted path shortened from the FRONT. What tells two nested columns apart
// is the end of the path: `…a.manual_campaign.` and `…a.cross_channel.` can
// agree for thirty characters and differ in the last five.
export function tail(text, max = 24) {
  return text.length > max ? `…${text.slice(-max)}` : text;
}

// Where the column ends up in the warehouse. A dotted path cannot be a column
// name there, so one is derived unless a name was given — either way it is not
// the name shown on the row, and that is worth saying. Nothing source-specific
// about it: `kunde.adresse.plz` lands as `kunde_adresse_plz` like anything else.
// It is also the name a primary key has to be written in, which is why the key
// picker offers these and not the paths.
export function landsAs(path, alias) {
  return alias || path.replace(/\./g, '_');
}

export function splitPath(path) {
  const dot = path.lastIndexOf('.');
  return dot < 0 ? ['', path] : [path.slice(0, dot + 1), path.slice(dot + 1)];
}

export const ColumnRow = React.memo(function ColumnRow({ column, checked }) {
  useTranslation();
  const badge = typeBadge(column);
  const [prefix, leaf] = splitPath(column.path);
  return (
    <div className="bq-row" data-path={column.path}>
      <input type="checkbox" checked={checked} readOnly tabIndex={-1} />
      <span className="bq-prefix">{tail(prefix)}</span>
      <span className="bq-leaf">{leaf}</span>
      <span className="bq-desc">{column.description}</span>
      <span className={`bq-type bq-${badge.tone}`}>{badge.label}</span>
    </div>
  );
});

/**
 * The scroll box the rows live in, and every style they use.
 *
 * Plain DOM inside a `styled` container rather than MUI components per row
 * (owner, 13.09, on a GA4 export of 171 columns: "about a second per
 * select-all"). Every row used to carry a Checkbox — a ButtonBase with a
 * ripple — a Chip, two Tooltips and five inline `sx` objects, and "select all"
 * re-renders every row, so the cost was paid 171 times per click, style
 * serialization included. Here the styles are one cached class and a row is
 * four DOM nodes.
 *
 * One click handler sits on THIS element and reads `data-path` off the row it
 * was fired from; rows take no callback, so React.memo actually holds and a
 * single tick re-renders one row instead of all of them.
 */
export const ColumnList = styled('div')(({ theme }) => ({
  maxHeight: 420,
  overflow: 'auto',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  '& .bq-row': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    cursor: 'pointer',
  },
  // Faint banding, so the eye can carry a name across the gap to its
  // description without a rule between every pair of rows.
  '& .bq-row:nth-of-type(even)': {
    backgroundColor: alpha(theme.palette.text.primary, 0.025),
  },
  '& .bq-row:hover': { backgroundColor: theme.palette.action.hover },
  '& .bq-row input': {
    accentColor: theme.palette.primary.main,
    margin: 0,
    flexShrink: 0,
    // The row owns the click. `readOnly` does not stop a checkbox from
    // flipping itself, and a box that flips on its own while React decides
    // otherwise is a box that disagrees with the state it is drawing.
    pointerEvents: 'none',
  },
  '& .bq-prefix, & .bq-leaf, & .bq-desc': {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: 12,
  },
  // The struct a column sits in, greyed and pushed RIGHT against the name.
  // Neighbouring GA4 columns share forty characters of prefix; at full
  // contrast they read as text to be read, and the list becomes a wall.
  // Right-aligning them also lines every NAME up at one x, which is what
  // makes a list of 171 scannable at all.
  // `0 1`: they never grow, so the alignment holds, but they do give way on a
  // narrow window rather than pushing the description off the row.
  '& .bq-prefix': {
    flex: '0 1 176px',
    textAlign: 'right',
    fontFamily: 'monospace',
    color: theme.palette.text.disabled,
  },
  '& .bq-leaf': { flex: '0 1 200px', fontFamily: 'monospace' },
  '& .bq-desc': { flex: '1 1 auto', color: theme.palette.text.secondary },
  '& .bq-type': {
    width: 88,
    flexShrink: 0,
    textAlign: 'center',
    fontSize: 11,
    lineHeight: '18px',
    borderRadius: 9,
    border: '1px solid currentColor',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .bq-muted': { color: theme.palette.text.disabled },
  ...Object.fromEntries(
    TONES.map((tone) => [`& .bq-${tone}`, { color: theme.palette[tone].main }]),
  ),
}));

/**
 * What the row had to shorten, in a panel that can be styled (owner, 13.09 —
 * the browser's own tooltip cannot be).
 *
 * DOCKED in the right-hand rail, not floating over the list. A floating card
 * anchors to the row, and a row is as wide as the list, so `right-start` put
 * the card past the edge of the window; every other placement covered the very
 * rows being read. Here it cannot go out of bounds, cannot cover anything, and
 * cannot be stranded by a scroll — it just sits in the same place and changes.
 *
 * It states four things in a fixed order and knows nothing about what any of
 * them mean: the whole path, the shape, the name the column lands under in the
 * warehouse, and whatever the table itself documents. Each part appears only
 * when there is something to say, so a table of invoice columns is described
 * exactly as a GA4 export is.
 */
export function ColumnCard({ column, alias }) {
  const { t } = useTranslation();
  if (!column) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{t('data.pointAtAColumnToSeeItsFullPathItsType')}</Typography>
    );
  }
  const badge = typeBadge(column);
  const [prefix, leaf] = splitPath(column.path);
  const lands = landsAs(column.path, alias);
  return (
    <>
      <Typography sx={{ fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}>
        <Box component="span" sx={{ color: 'text.disabled' }}>
          {prefix}
        </Box>
        {leaf}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
        <Chip
          size="small"
          variant="outlined"
          color={badge.tone === 'muted' ? 'default' : badge.tone}
          label={(column.type || t('data.unknownType')).toLowerCase()}
        />
        {column.repeated && <Chip size="small" variant="outlined" label={t('data.repeated')} />}
        {column.carried_as === 'json' && (
          <Chip size="small" variant="outlined" color="primary" label={t('data.jsonText')} />
        )}
      </Box>
      {lands !== column.path && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{t('data.loadsAs')}{' '}
          <Box
            component="span"
            sx={{ fontFamily: 'monospace', color: 'text.primary', wordBreak: 'break-all' }}
          >
            {lands}
          </Box>
        </Typography>
      )}
      {column.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {column.description}
        </Typography>
      )}
    </>
  );
}

/**
 * The column list. Hovering a row reports it upwards, so the rail can describe
 * it; no delay, because a docked panel changing is not a card appearing over
 * what you were reading.
 */
export function ColumnTable({ columns, picked, onToggle, onInspect, emptyNote }) {
  useTranslation();
  const hovered = useRef(null);

  const onMouseOver = (e) => {
    const row = e.target.closest?.('[data-path]');
    const path = row ? row.dataset.path : null;
    if (hovered.current === path) return;
    hovered.current = path;
    onInspect(path ? columns.find((c) => c.path === path) : null);
  };

  const hide = () => {
    if (hovered.current === null) return;
    hovered.current = null;
    onInspect(null);
  };

  return (
    <ColumnList
      onClick={(e) => {
        const row = e.target.closest?.('[data-path]');
        if (row) onToggle(row.dataset.path);
      }}
      onMouseOver={onMouseOver}
      onMouseLeave={hide}
    >
      {columns.map((column) => (
        <ColumnRow key={column.path} column={column} checked={picked.has(column.path)} />
      ))}
      {!columns.length && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', p: 1.5 }}
        >
          {emptyNote}
        </Typography>
      )}
    </ColumnList>
  );
}
